import { useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import { EmptyState, TableSkeleton } from '../components/States';
import { useTranslation } from 'react-i18next';
import type { CatalogImportResult, CatalogImportRow, Granularity, Lifecycle, PriceHistoryEntry, Product, ProductDetail, Variant } from '@teo/shared';
import { api, ApiRequestError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Badge, Panel, type BadgeTone } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { Pager } from '../components/Pager';
import { useLoad, useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate, formatMoney, formatNumber } from '../lib/format';
import { parseCatalogWorkbook, type ParsedCatalog } from '../lib/excel';
import type { Language } from '../i18n';

const LIFECYCLES: Lifecycle[] = ['ACTIVA', 'AGOTANDOSE', 'TEMPORAL', 'DESCONTINUADA', 'ARCHIVADA'];
const CATEGORIES = ['Camiseta', 'Oversize', 'Chanclas', 'Pantalonera', 'Jean', 'Jogger', 'Conjunto', 'Sudadera', 'Blusa', 'Otro'];
const ORIGINS = ['China', 'Nacional', 'Mixto', 'Por definir'];
const INTERNAL_FACTOR = 0.85;

interface ProductForm {
  id: number | null;
  ref: string;
  barcode: string;
  name: string;
  brand: string;
  line: string;
  category: string;
  gender: string;
  origin: string;
  min_stock: number;
  stock_granularity: Granularity;
  notes: string;
  unit_cost: string;
  internal_price: string;
  wholesale_price: string;
  retail_price: string;
  promo_price: string;
  min_sale_price: string;
  colors: string;
  sizes: string;
  /** Precio interno escrito a mano (no se vuelve a sugerir). */
  internalManual: boolean;
}

const EMPTY: ProductForm = {
  id: null, ref: '', barcode: '', name: '', brand: 'Masters 85', line: 'Masters 85', category: 'Camiseta', gender: '', origin: 'Por definir',
  min_stock: 10, stock_granularity: 'COLOR_SIZE', notes: '', unit_cost: '', internal_price: '', wholesale_price: '', retail_price: '',
  promo_price: '', min_sale_price: '', colors: '', sizes: 'S, M, L, XL', internalManual: false,
};

const num = (s: string) => (s.trim() === '' ? undefined : Number(s));
const list = (s: string) => s.split(/[,;\n]/).map((x) => x.trim()).filter(Boolean);

/** Combinación color × talla según la granularidad (exportada para pruebas). */
export function buildVariants(g: Granularity, colors: string[], sizes: string[]): { color?: string; size?: string }[] {
  if (g === 'NONE') return [];
  if (g === 'COLOR') return colors.map((color) => ({ color }));
  return colors.flatMap((color) => sizes.map((size) => ({ color, size })));
}

/** Sugerencia del precio interno (distribuidor) = por mayor × 0,85, redondeado a pesos. */
/** Sugerencia del precio distribuidor: 85 % del mayorista, nunca por debajo del costo (regla del cliente). Solo se usa en campos vacíos. */
export const suggestInternal = (wholesale: number, cost = 0) => Math.max(Math.round(wholesale * INTERNAL_FACTOR), cost > 0 ? cost : 0);

const PRICE_FIELDS = ['unit_cost', 'internal_price', 'wholesale_price', 'retail_price', 'promo_price', 'min_sale_price'] as const;
export type PriceField = (typeof PRICE_FIELDS)[number];
export interface PriceChange {
  field: PriceField;
  from: number | null;
  to: number;
}

/** Qué cambió en una fila del historial frente a la anterior (la más antigua se compara contra nada: precios iniciales). */
export function priceChanges(entry: PriceHistoryEntry, previous: PriceHistoryEntry | undefined): PriceChange[] {
  const out: PriceChange[] = [];
  for (const field of PRICE_FIELDS) {
    const to = entry[field];
    if (to == null) continue; // costo oculto para quien no tiene cost.view
    if (!previous) {
      if (to > 0) out.push({ field, from: null, to });
      continue;
    }
    const from = previous[field] ?? 0;
    if (from !== to) out.push({ field, from, to });
  }
  return out;
}

/** Precios de venta (> 0) por debajo del costo: TEO no los corrige, solo los marca para Gerencia. */
export function pricesBelowCost(f: { unit_cost: string; internal_price: string; wholesale_price: string; retail_price: string; promo_price: string }): string[] {
  const cost = Number(f.unit_cost) || 0;
  if (cost <= 0) return [];
  // La promoción no entra: puede ir por debajo del costo a propósito (decisión del cliente).
  return (['internal_price', 'wholesale_price', 'retail_price'] as const).filter((k) => {
    const v = Number(f[k]) || 0;
    return v > 0 && v < cost;
  });
}

function lifecycleTone(l: Lifecycle): BadgeTone {
  return l === 'ACTIVA' ? 'green' : l === 'ARCHIVADA' ? 'red' : 'amber';
}

/** Vista "Productos y precios" del prototipo: catálogo, ficha del producto, curvas e importación. */
export function Products() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  const { can } = useAuth();
  const { show, showError } = useToast();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const products = useLoad(
    () => api.products({ q: q || undefined, lifecycle: status || undefined, include_archived: false, limit: pageSize, offset: (page - 1) * pageSize }),
    [q, status, page, pageSize],
  );
  // La alerta de precios bajo costo cubre todo el catálogo, no solo la página visible.
  const canSeeCost = can('cost.view');
  const anomalies = useLoad(() => (canSeeCost ? api.products({ below_cost: true, limit: 500 }) : Promise.resolve({ items: [], total: 0 })), [canSeeCost]);
  const curves = useLoad(() => api.curves());
  const { busy, run } = useSubmit();

  const [form, setForm] = useState<ProductForm | null>(null);
  const [detail, setDetail] = useState<ProductDetail | null>(null);
  const [similar, setSimilar] = useState<{ ref: string; name: string } | null>(null);
  const [addVar, setAddVar] = useState({ colors: '', sizes: '' });
  const [lifecycleForm, setLifecycleForm] = useState<{ lifecycle: Lifecycle; reason: string } | null>(null);
  const [curveModal, setCurveModal] = useState(false);
  const [importModal, setImportModal] = useState(false);

  const canEdit = can('catalog.edit');
  // El usuario de Masters solo maneja su marca: el servidor lo obliga y aquí se bloquea el campo.
  const mastersOnly = can('catalog.masters_only');
  const canImport = can('import.catalog');
  const seesCost = canSeeCost;

  const openNew = () => {
    setDetail(null);
    setForm(EMPTY);
    // Propone el consecutivo que sigue; el usuario puede cambiarlo. Si falla, se deja vacío.
    void api.nextRef().then((r) => setForm((f) => (f && f.id === null && f.ref === '' ? { ...f, ref: r.ref } : f))).catch(() => undefined);
  };
  const openEdit = async (p: Product) => {
    try {
      const d = await api.product(p.id);
      setDetail(d);
      setForm({
        id: d.id, ref: d.ref, barcode: d.barcode ?? '', name: d.name, brand: d.brand, line: d.line, category: d.category, gender: d.gender,
        origin: d.origin, min_stock: d.min_stock, stock_granularity: d.stock_granularity, notes: d.notes,
        unit_cost: d.unit_cost == null ? '' : String(d.unit_cost), internal_price: String(d.internal_price ?? ''),
        wholesale_price: String(d.wholesale_price ?? ''), retail_price: String(d.retail_price ?? ''),
        promo_price: String(d.promo_price ?? ''), min_sale_price: String(d.min_sale_price ?? ''), colors: '', sizes: '',
        // Regla del cliente: el 85 % solo se sugiere cuando el precio distribuidor está vacío (0); un precio existente nunca se recalcula.
        internalManual: (d.internal_price ?? 0) > 0,
      });
    } catch (err) {
      showError(err);
    }
  };

  const setWholesale = (v: string) => {
    if (!form) return;
    const w = Number(v);
    setForm({ ...form, wholesale_price: v, internal_price: form.internalManual || !Number.isFinite(w) || v === '' ? form.internal_price : String(suggestInternal(w, Number(form.unit_cost) || 0)) });
  };

  const scope = form?.id ? `producto:${form.id}` : 'producto:nuevo';

  const save = async (confirmSimilar: boolean) => {
    if (!form) return;
    await run(async () => {
      try {
        const key = requestKey(scope);
        const prices = {
          unit_cost: num(form.unit_cost), internal_price: num(form.internal_price), wholesale_price: num(form.wholesale_price),
          retail_price: num(form.retail_price), promo_price: num(form.promo_price), min_sale_price: num(form.min_sale_price),
        };
        if (form.id) {
          await api.updateProduct(key, form.id, {
            name: form.name, brand: form.brand, line: form.line, category: form.category, gender: form.gender, origin: form.origin,
            min_stock: form.min_stock, notes: form.notes, prices, confirm_similar: confirmSimilar,
          });
        } else {
          await api.createProduct(key, {
            ref: form.ref, barcode: form.barcode || undefined, name: form.name, brand: form.brand, line: form.line, category: form.category,
            gender: form.gender, origin: form.origin, min_stock: form.min_stock, stock_granularity: form.stock_granularity, notes: form.notes,
            prices, variants: buildVariants(form.stock_granularity, list(form.colors), list(form.sizes)), confirm_similar: confirmSimilar,
          });
        }
        releaseKey(scope);
        setSimilar(null);
        setForm(null);
        setDetail(null);
        show(t('products.saved'));
        await Promise.all([products.reload(), anomalies.reload()]);
      } catch (err) {
        if (err instanceof ApiRequestError && err.code === 'PRODUCT_NAME_SIMILAR') {
          setSimilar({ ref: String(err.params.similar_ref ?? ''), name: String(err.params.similar_name ?? '') });
        } else showError(err);
      }
    });
  };

  const submitVariants = async (e: FormEvent) => {
    e.preventDefault();
    if (!detail) return;
    const s = `producto:${detail.id}:variantes`;
    await run(async () => {
      try {
        await api.addVariants(requestKey(s), detail.id, buildVariants(detail.stock_granularity, list(addVar.colors), list(addVar.sizes)));
        releaseKey(s);
        setAddVar({ colors: '', sizes: '' });
        setDetail(await api.product(detail.id));
        show(t('products.variantsAdded'));
      } catch (err) {
        showError(err);
      }
    });
  };

  const [control, setControl] = useState<{ granularity: Granularity; colors: string; sizes: string }>({ granularity: 'COLOR_SIZE', colors: '', sizes: 'S, M, L, XL' });
  const submitControl = async (e: FormEvent) => {
    e.preventDefault();
    if (!detail) return;
    const s = `producto:${detail.id}:control`;
    await run(async () => {
      try {
        const r = await api.changeControl(requestKey(s), detail.id, control.granularity, buildVariants(control.granularity, list(control.colors), list(control.sizes)));
        releaseKey(s);
        const fresh = await api.product(detail.id);
        setDetail(fresh);
        if (form) setForm({ ...form, stock_granularity: fresh.stock_granularity });
        show(t('products.controlDone', { n: r.variants }));
        await products.reload();
      } catch (err) {
        showError(err);
      }
    });
  };

  const submitLifecycle = async (e: FormEvent) => {
    e.preventDefault();
    if (!detail || !lifecycleForm) return;
    const s = `producto:${detail.id}:estado:${lifecycleForm.lifecycle}`;
    await run(async () => {
      try {
        await api.setLifecycle(requestKey(s), detail.id, lifecycleForm.lifecycle, lifecycleForm.reason);
        releaseKey(s);
        setLifecycleForm(null);
        setForm(null);
        setDetail(null);
        show(t('products.lifecycleSaved'));
        await Promise.all([products.reload(), anomalies.reload()]);
      } catch (err) {
        showError(err);
      }
    });
  };

  const belowCost = anomalies.data?.items ?? [];
  /** Celda de precio: a la derecha, y resaltada cuando ese precio es el que está por debajo del costo. */
  const low = (p: Product, field: string) => (seesCost && p.below_cost_fields?.includes(field) ? 'num lowprice' : 'num');
  const lowNames = (p: Product) => (p.below_cost_fields ?? []).map((k) => t(`products.priceNames.${k}`)).join(', ');
  const rows = products.data?.items ?? [];
  const total = products.data?.total ?? 0;
  const margin = (p: Product) => (seesCost && p.unit_cost != null && p.retail_price ? ((p.retail_price - p.unit_cost) / p.retail_price) * 100 : null);

  return (
    <>
      <Panel
        title={t('products.title')}
        description={t('products.description')}
        badge={
          <div className="actions">
            {canImport ? (
              <button type="button" className="btn" id="importCatalogBtn" onClick={() => setImportModal(true)}>
                {t('products.import')}
              </button>
            ) : null}
            {canEdit ? (
              <button type="button" className="btn black" id="newProduct" onClick={openNew}>
                {t('products.new')}
              </button>
            ) : null}
          </div>
        }
      >
        <div className="catalogfilters">
          <input className="input" id="productSearch" placeholder={t('products.searchPlaceholder')} value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
          <select className="select" id="productStatusFilter" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            <option value="">{t('products.allStatuses')}</option>
            {LIFECYCLES.map((l) => (
              <option key={l} value={l}>
                {t(`lifecycle.${l}`)}
              </option>
            ))}
          </select>
        </div>
        {seesCost && belowCost.length > 0 ? (
          <div className="alerts" id="belowCostAlert" style={{ marginBottom: 10 }}>
            <div className="alert">
              <div>
                <b>{t('products.belowCostAlertTitle', { n: belowCost.length })}</b>
                <small id="belowCostDetail">{t('products.belowCostAlertHelp', { refs: belowCost.slice(0, 12).map((p) => t('products.belowCostRef', { ref: p.ref, fields: lowNames(p) })).join(' · ') })}</small>
              </div>
              <Badge tone="red">{t('products.belowCost')}</Badge>
            </div>
          </div>
        ) : null}
        <div className="tablewrap">
          <table>
            <thead>
              <tr>
                <th>{t('products.cols.ref')}</th>
                <th>{t('products.cols.product')}</th>
                <th>{t('products.cols.brand')}</th>
                <th>{t('products.cols.control')}</th>
                {seesCost ? <th className="num">{t('products.cols.cost')}</th> : null}
                <th className="num">{t('products.cols.internal')}</th>
                <th className="num">{t('products.cols.wholesale')}</th>
                <th className="num">{t('products.cols.retail')}</th>
                <th className="num">{t('products.cols.promo')}</th>
                <th className="num">{t('products.cols.minSale')}</th>
                {seesCost ? <th className="num">{t('products.cols.margin')}</th> : null}
                <th>{t('common.status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody id="productBody">
              {products.loading && !products.data ? <TableSkeleton cols={13} /> : null}
              {rows.map((p) => {
                const m = margin(p);
                return (
                  <tr key={p.id} data-ref={p.ref}>
                    <td>
                      <b>{p.ref}</b>
                    </td>
                    <td>
                      {p.name}
                      <br />
                      <span className="tiny muted">{p.category}</span>
                    </td>
                    <td>{p.brand}</td>
                    <td>
                      {t(`granularity.${p.stock_granularity}`)}
                      <br />
                      <span className="tiny muted">{t('products.variantCount', { n: p.variant_count })}</span>
                    </td>
                    {seesCost ? <td className="num">{p.unit_cost ? formatMoney(p.unit_cost, lang) : '—'}</td> : null}
                    <td className={low(p, 'internal_price')}>{p.internal_price ? formatMoney(p.internal_price, lang) : '—'}</td>
                    <td className={low(p, 'wholesale_price')}>{p.wholesale_price ? formatMoney(p.wholesale_price, lang) : '—'}</td>
                    <td className={low(p, 'retail_price')}>{p.retail_price ? formatMoney(p.retail_price, lang) : '—'}</td>
                    <td className={low(p, 'promo_price')}>{p.promo_price ? formatMoney(p.promo_price, lang) : '—'}</td>
                    <td className="num">{p.min_sale_price ? formatMoney(p.min_sale_price, lang) : '—'}</td>
                    {seesCost ? <td className="num">{m == null ? '—' : `${formatNumber(Math.round(m), lang)}%`}</td> : null}
                    <td>
                      <Badge tone={lifecycleTone(p.lifecycle)}>{t(`lifecycle.${p.lifecycle}`)}</Badge>
                      {seesCost && p.below_cost ? (
                        <>
                          {' '}
                          <span title={lowNames(p)}><Badge tone="red">{t('products.belowCostBadge', { fields: lowNames(p) })}</Badge></span>
                        </>
                      ) : null}
                    </td>
                    <td>
                      <button type="button" className="btn sm" onClick={() => void openEdit(p)}>
                        {canEdit ? t('common.edit') : t('common.view')}
                      </button>
                    </td>
                  </tr>
                );
              })}
              {products.data && rows.length === 0 ? (
                <tr>
                  <td colSpan={13} className="empty">
                    <EmptyState icon="products" title={t('products.empty')} hint={t('products.emptyHint')} />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
        <Pager id="productPager" page={page} pageSize={pageSize} total={total} onPage={setPage} onPageSize={(n) => { setPageSize(n); setPage(1); }} />
      </Panel>

      <div className="panel" style={{ marginTop: 14 }}>
        <div className="head">
          <div>
            <h3>{t('curves.title')}</h3>
            <p>{t('curves.description')}</p>
          </div>
          {canEdit ? (
            <button type="button" className="btn black" id="newCurve" onClick={() => setCurveModal(true)}>
              {t('curves.new')}
            </button>
          ) : null}
        </div>
        <div className="alerts" id="curveList">
          {(curves.data ?? []).map((c) => (
            <div className="alert" key={c.id} data-curve={c.code}>
              <div>
                <b>
                  {c.code} · {c.name}
                </b>
                <small>
                  {t('curves.summary', { lines: c.lines.length, units: c.total_units, half: c.total_units / 2 })}
                </small>
              </div>
              <Badge tone={c.active ? 'green' : 'red'}>{c.active ? t('common.active') : t('common.inactive')}</Badge>
            </div>
          ))}
          {curves.data && curves.data.length === 0 ? <div className="empty">{t('curves.empty')}</div> : null}
        </div>
      </div>

      {/* Ficha del producto (modal del prototipo) */}
      <Modal open={form !== null && !similar} title={form?.id ? t('products.editTitle') : t('products.newTitle')} help={t('products.modalHelp')} onClose={() => { setForm(null); setDetail(null); }}>
        {form ? (
          <form
            className="formgrid"
            id="productForm"
            onSubmit={(e) => {
              e.preventDefault();
              void save(false);
            }}
          >
            <div className="field">
              <label htmlFor="prRef">{t('products.fields.ref')}</label>
              <input id="prRef" required readOnly={!!form.id} value={form.ref} onChange={(e) => setForm({ ...form, ref: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="prBarcode">{t('products.fields.barcode')}</label>
              <input id="prBarcode" readOnly={!!form.id} value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="prBrand">{t('products.fields.brand')}</label>
              <input id="prBrand" readOnly={!canEdit || mastersOnly} value={mastersOnly ? 'Masters 85' : form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="prLine">{t('products.fields.line')}</label>
              <input id="prLine" readOnly={!canEdit} value={form.line} onChange={(e) => setForm({ ...form, line: e.target.value })} />
            </div>
            <div className="field span2">
              <label htmlFor="prName">{t('products.fields.name')}</label>
              <input id="prName" required readOnly={!canEdit} placeholder={t('products.fields.namePlaceholder')} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="prCategory">{t('products.fields.category')}</label>
              <input id="prCategory" list="prCategories" readOnly={!canEdit} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
              <datalist id="prCategories">
                {CATEGORIES.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div className="field">
              <label htmlFor="prOrigin">{t('products.fields.origin')}</label>
              <select id="prOrigin" disabled={!canEdit} value={form.origin} onChange={(e) => setForm({ ...form, origin: e.target.value })}>
                {ORIGINS.map((o) => (
                  <option key={o} value={o}>
                    {o}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="prGender">{t('products.fields.gender')}</label>
              <input id="prGender" readOnly={!canEdit} value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="prMin">{t('products.fields.minStock')}</label>
              <input id="prMin" type="number" min={0} readOnly={!canEdit} value={form.min_stock} onChange={(e) => setForm({ ...form, min_stock: Number(e.target.value) })} />
            </div>
            <div className="field span2">
              <label htmlFor="prGranularity">{t('products.fields.granularity')}</label>
              <select id="prGranularity" disabled={!!form.id} value={form.stock_granularity} onChange={(e) => setForm({ ...form, stock_granularity: e.target.value as Granularity })}>
                <option value="NONE">{t('granularity.NONE')}</option>
                <option value="COLOR">{t('granularity.COLOR')}</option>
                <option value="COLOR_SIZE">{t('granularity.COLOR_SIZE')}</option>
              </select>
              <div className="help">{t('products.fields.granularityHelp')}</div>
            </div>
            {!form.id && form.stock_granularity !== 'NONE' ? (
              <div className="field">
                <label htmlFor="prColors">{t('products.fields.colors')}</label>
                <input id="prColors" required placeholder={t('products.fields.colorsPlaceholder')} value={form.colors} onChange={(e) => setForm({ ...form, colors: e.target.value })} />
              </div>
            ) : null}
            {!form.id && form.stock_granularity === 'COLOR_SIZE' ? (
              <div className="field">
                <label htmlFor="prSizes">{t('products.fields.sizes')}</label>
                <input id="prSizes" required placeholder={t('products.fields.sizesPlaceholder')} value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} />
              </div>
            ) : null}
            {!form.id && form.stock_granularity !== 'NONE' ? (
              <div className="help span2" id="variantPreview">
                {t('products.variantPreview', { n: buildVariants(form.stock_granularity, list(form.colors), list(form.sizes)).length })}
              </div>
            ) : null}

            {seesCost ? (
              <div className="field">
                <label htmlFor="prCost">{t('products.fields.cost')}</label>
                <input id="prCost" type="number" min={0} readOnly={!canEdit} value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} />
              </div>
            ) : null}
            <div className="field">
              <label htmlFor="prWholesale">{t('products.fields.wholesale')}</label>
              <input id="prWholesale" type="number" min={0} readOnly={!canEdit} value={form.wholesale_price} onChange={(e) => setWholesale(e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="prInternal">{t('products.fields.internal')}</label>
              <input id="prInternal" type="number" min={0} readOnly={!canEdit} value={form.internal_price} onChange={(e) => setForm({ ...form, internal_price: e.target.value, internalManual: (Number(e.target.value) || 0) > 0 })} />
              <div className="help">{t('products.fields.internalHelp')}</div>
              {canEdit && !(Number(form.internal_price) > 0) && Number(form.wholesale_price) > 0 ? (
                <button
                  type="button" className="btn sm" id="useSuggestedInternal" style={{ marginTop: 6 }}
                  onClick={() => setForm({ ...form, internal_price: String(suggestInternal(Number(form.wholesale_price), Number(form.unit_cost) || 0)), internalManual: true })}
                >
                  {t('products.useSuggested', { price: formatMoney(suggestInternal(Number(form.wholesale_price), Number(form.unit_cost) || 0), lang) })}
                </button>
              ) : null}
            </div>
            <div className="field">
              <label htmlFor="prRetail">{t('products.fields.retail')}</label>
              <input id="prRetail" type="number" min={0} readOnly={!canEdit} value={form.retail_price} onChange={(e) => setForm({ ...form, retail_price: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="prPromo">{t('products.fields.promo')}</label>
              <input id="prPromo" type="number" min={0} readOnly={!canEdit} value={form.promo_price} onChange={(e) => setForm({ ...form, promo_price: e.target.value })} />
            </div>
            <div className="field">
              <label htmlFor="prMinSale">{t('products.fields.minSale')}</label>
              <input id="prMinSale" type="number" min={0} readOnly={!canEdit} value={form.min_sale_price} onChange={(e) => setForm({ ...form, min_sale_price: e.target.value })} />
            </div>
            {seesCost && pricesBelowCost(form).length > 0 ? (
              <div className="pricewarning span2" id="belowCostWarning">
                {t('products.belowCostWarning', { fields: pricesBelowCost(form).map((k) => t(`products.priceNames.${k}`)).join(', ') })}
              </div>
            ) : null}
            <div className="field span2">
              <label htmlFor="prNotes">{t('products.fields.notes')}</label>
              <input id="prNotes" readOnly={!canEdit} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            {canEdit ? (
              <button className="btn black span2" type="submit" id="productSave" disabled={busy}>
                {busy ? t('common.saving') : t('common.save')}
              </button>
            ) : null}
          </form>
        ) : null}

        {detail ? (
          <div className="g2" style={{ marginTop: 12 }}>
            <div>
              <h4>{t('products.variants.title')}</h4>
              <div className="tablewrap v52-modal-table">
                <table>
                  <thead>
                    <tr>
                      <th>{t('products.variants.color')}</th>
                      <th>{t('products.variants.size')}</th>
                      <th>{t('products.variants.sku')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.variants.map((v: Variant) => (
                      <tr key={v.id}>
                        <td>{v.color ?? '—'}</td>
                        <td>{v.size ?? '—'}</td>
                        <td>{v.sku}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {canEdit && detail.stock_granularity !== 'NONE' ? (
                <form className="actions" style={{ marginTop: 8 }} onSubmit={(e) => void submitVariants(e)}>
                  <input className="input" placeholder={t('products.fields.colorsPlaceholder')} value={addVar.colors} onChange={(e) => setAddVar({ ...addVar, colors: e.target.value })} required />
                  {detail.stock_granularity === 'COLOR_SIZE' ? (
                    <input className="input" placeholder={t('products.fields.sizesPlaceholder')} value={addVar.sizes} onChange={(e) => setAddVar({ ...addVar, sizes: e.target.value })} required />
                  ) : null}
                  <button className="btn sm" type="submit" disabled={busy}>
                    {t('products.variants.add')}
                  </button>
                </form>
              ) : null}
              {canEdit && detail.can_change_control ? (
                <form id="controlForm" style={{ marginTop: 12 }} onSubmit={(e) => void submitControl(e)}>
                  <h4>{t('products.controlTitle')}</h4>
                  <div className="help" style={{ marginBottom: 6 }}>{t('products.controlHelp')}</div>
                  <div className="actions">
                    <select className="select" id="controlGranularity" aria-label={t('products.fields.granularity')} value={control.granularity} onChange={(e) => setControl({ ...control, granularity: e.target.value as Granularity })}>
                      {(['NONE', 'COLOR', 'COLOR_SIZE'] as const).map((g) => (
                        <option key={g} value={g}>
                          {t(`granularity.${g}`)}
                        </option>
                      ))}
                    </select>
                    {control.granularity !== 'NONE' ? (
                      <input className="input" id="controlColors" placeholder={t('products.fields.colorsPlaceholder')} value={control.colors} onChange={(e) => setControl({ ...control, colors: e.target.value })} required />
                    ) : null}
                    {control.granularity === 'COLOR_SIZE' ? (
                      <input className="input" id="controlSizes" placeholder={t('products.fields.sizesPlaceholder')} value={control.sizes} onChange={(e) => setControl({ ...control, sizes: e.target.value })} required />
                    ) : null}
                    <button className="btn sm black" type="submit" id="controlApply" disabled={busy}>
                      {t('products.controlApply')}
                    </button>
                  </div>
                </form>
              ) : null}
              {canEdit ? (
                <div className="actions" style={{ marginTop: 10 }}>
                  <button type="button" className="btn sm" id="lifecycleBtn" onClick={() => setLifecycleForm({ lifecycle: detail.lifecycle, reason: '' })}>
                    {t('products.lifecycle.change')}
                  </button>
                </div>
              ) : null}
            </div>
            <div>
              <h4>{t('products.history.title')}</h4>
              <div id="priceHistoryBox">
                {detail.price_history.map((h, idx) => {
                  const changes = priceChanges(h, detail.price_history[idx + 1]);
                  return (
                    <div className="historyrow" key={h.id}>
                      <div>
                        <b>{formatDate(h.effective_at, lang)}</b>
                        <br />
                        <span className="muted">{[h.user_name, h.source === 'manual' ? t('products.history.manual') : h.source].filter(Boolean).join(' · ')}</span>
                      </div>
                      <div>
                        {idx === detail.price_history.length - 1 ? <span className="muted">{t('products.history.initial')}</span> : null}
                        {changes.map((c) => (
                          <div key={c.field} data-change={c.field}>
                            {c.from == null
                              ? t('products.history.set', { field: t(`products.history.fields.${c.field}`), to: formatMoney(c.to, lang) })
                              : t('products.history.change', { field: t(`products.history.fields.${c.field}`), from: formatMoney(c.from, lang), to: formatMoney(c.to, lang) })}
                          </div>
                        ))}
                        {changes.length === 0 ? <span className="muted">{t('products.history.noChange')}</span> : null}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </Modal>

      {/* Nombre similar: mismo flujo del prototipo */}
      <Modal open={similar !== null} title={t('products.similar.title')} help={t('products.similar.help')} onClose={() => setSimilar(null)}>
        <div className="v52-note v52-warning" id="similarProductInfo">
          {similar ? t('products.similar.info', { ref: similar.ref, name: similar.name }) : ''}
        </div>
        <div className="actions" style={{ marginTop: 12 }}>
          <button type="button" className="btn" id="similarReview" onClick={() => setSimilar(null)}>
            {t('products.similar.review')}
          </button>
          <button type="button" className="btn black" id="similarContinue" disabled={busy} onClick={() => void save(true)}>
            {t('products.similar.continue')}
          </button>
        </div>
      </Modal>

      <Modal open={lifecycleForm !== null} title={t('products.lifecycle.title')} onClose={() => setLifecycleForm(null)}>
        {lifecycleForm ? (
          <form className="formgrid" onSubmit={(e) => void submitLifecycle(e)}>
            <div className="field">
              <label htmlFor="lcState">{t('products.lifecycle.state')}</label>
              <select id="lcState" value={lifecycleForm.lifecycle} onChange={(e) => setLifecycleForm({ ...lifecycleForm, lifecycle: e.target.value as Lifecycle })}>
                {LIFECYCLES.map((l) => (
                  <option key={l} value={l}>
                    {t(`lifecycle.${l}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="lcReason">{t('products.lifecycle.reason')}</label>
              <input id="lcReason" value={lifecycleForm.reason} onChange={(e) => setLifecycleForm({ ...lifecycleForm, reason: e.target.value })} />
            </div>
            <div className="help span2">{t('products.lifecycle.archiveHelp')}</div>
            <button className="btn black span2" type="submit" disabled={busy}>
              {t('common.save')}
            </button>
          </form>
        ) : null}
      </Modal>

      <CurveModal open={curveModal} onClose={() => setCurveModal(false)} onSaved={() => void curves.reload()} />
      <ImportModal open={importModal} onClose={() => setImportModal(false)} onImported={() => { void products.reload(); void anomalies.reload(); }} />
    </>
  );
}

/** Nueva curva: paquete de compra (variantes × unidades). */
function CurveModal({ open, onClose, onSaved }: { open: boolean; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [q, setQ] = useState('');
  const [found, setFound] = useState<Variant[]>([]);
  const [lines, setLines] = useState<{ variant: Variant; units: number }[]>([]);
  const [units, setUnits] = useState(12);

  const search = async (value: string) => {
    setQ(value);
    if (value.trim().length < 2) return setFound([]);
    try {
      setFound(await api.variants(value.trim()));
    } catch (err) {
      showError(err);
    }
  };
  const add = (v: Variant) => {
    if (lines.some((l) => l.variant.id === v.id)) return;
    setLines([...lines, { variant: v, units }]);
  };
  const total = useMemo(() => lines.reduce((a, l) => a + l.units, 0), [lines]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    await run(async () => {
      try {
        await api.createCurve(requestKey('curva:nueva'), { code, name, active: true, lines: lines.map((l) => ({ variant_id: l.variant.id, units: l.units })) });
        releaseKey('curva:nueva');
        setCode('');
        setName('');
        setLines([]);
        show(t('curves.saved'));
        onSaved();
        onClose();
      } catch (err) {
        showError(err);
      }
    });
  };

  return (
    <Modal open={open} title={t('curves.newTitle')} help={t('curves.help')} onClose={onClose}>
      <form className="formgrid" id="curveForm" onSubmit={(e) => void submit(e)}>
        <div className="field">
          <label htmlFor="cvCode">{t('curves.fields.code')}</label>
          <input id="cvCode" required value={code} onChange={(e) => setCode(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cvName">{t('curves.fields.name')}</label>
          <input id="cvName" required value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cvSearch">{t('curves.fields.search')}</label>
          <input id="cvSearch" placeholder={t('curves.fields.searchPlaceholder')} value={q} onChange={(e) => void search(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="cvUnits">{t('curves.fields.units')}</label>
          <input id="cvUnits" type="number" min={1} value={units} onChange={(e) => setUnits(Math.max(1, Number(e.target.value)))} />
        </div>
        {found.length ? (
          <div className="span2 tablewrap v52-modal-table">
            <table>
              <tbody>
                {found.slice(0, 30).map((v) => (
                  <tr key={v.id}>
                    <td>
                      <b>{v.ref}</b> {v.product_name}
                    </td>
                    <td>{[v.color, v.size].filter(Boolean).join(' / ') || '—'}</td>
                    <td>
                      <button type="button" className="btn sm" onClick={() => add(v)}>
                        {t('common.add')}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <div className="span2 tablewrap v52-modal-table" id="curveLines">
          <table>
            <thead>
              <tr>
                <th>{t('curves.fields.line')}</th>
                <th>{t('curves.fields.units')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => (
                <tr key={l.variant.id}>
                  <td>
                    {l.variant.sku}
                  </td>
                  <td>
                    <input type="number" min={1} value={l.units} onChange={(e) => setLines(lines.map((x, j) => (j === i ? { ...x, units: Math.max(1, Number(e.target.value)) } : x)))} />
                  </td>
                  <td>
                    <button type="button" className="btn sm" onClick={() => setLines(lines.filter((_, j) => j !== i))}>
                      {t('common.remove')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="help span2">{t('curves.summary', { lines: lines.length, units: total, half: total / 2 })}</div>
        <button className="btn black span2" type="submit" disabled={busy || lines.length === 0}>
          {t('common.save')}
        </button>
      </form>
    </Modal>
  );
}

/** Importación de catálogo y precios desde Excel: lectura local, vista previa en el servidor y aplicación todo o nada. */
function ImportModal({ open, onClose, onImported }: { open: boolean; onClose: () => void; onImported: () => void }) {
  const { t } = useTranslation();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const [parsed, setParsed] = useState<ParsedCatalog | null>(null);
  const [fileName, setFileName] = useState('');
  const [preview, setPreview] = useState<CatalogImportResult | null>(null);

  const onFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(null);
    try {
      const p = parseCatalogWorkbook(await file.arrayBuffer());
      setParsed(p);
      setFileName(file.name);
      if (p.rows.length === 0) {
        show(t('import.noRows'));
        return;
      }
      setPreview(await api.importCatalogPreview(p.rows as CatalogImportRow[], file.name));
    } catch (err) {
      showError(err);
    }
  };

  const commit = async () => {
    if (!parsed || !preview) return;
    await run(async () => {
      const scope = `importar:${fileName}:${parsed.rows.length}`;
      try {
        const r = await api.importCatalogCommit(requestKey(scope), parsed.rows as CatalogImportRow[], fileName);
        releaseKey(scope);
        show(t('import.done', { new: r.new, updated: r.updated }));
        setParsed(null);
        setPreview(null);
        onImported();
        onClose();
      } catch (err) {
        showError(err);
      }
    });
  };

  const rowsWithIssues = preview?.rows.filter((r) => r.status === 'error') ?? [];

  return (
    <Modal open={open} title={t('import.title')} help={t('import.help')} onClose={onClose}>
      <div className="formgrid">
        <div className="field span2">
          <label htmlFor="importFile">{t('import.file')}</label>
          <input id="importFile" type="file" accept=".xlsx,.xls,.csv" onChange={(e) => void onFile(e)} />
        </div>
        {parsed ? (
          <div className="v52-note span2" id="importSummary">
            {parsed.sheets.map((s) => `${s.name}: ${s.rows}`).join(' · ')}
            {parsed.skipped.length ? ` · ${t('import.skipped', { sheets: parsed.skipped.join(', ') })}` : ''}
          </div>
        ) : null}
        {preview ? (
          <>
            <div className="kpis span2" id="importPreview">
              <div className="kpi">
                <div className="lab">{t('import.new')}</div>
                <div className="val">{preview.new}</div>
              </div>
              <div className="kpi">
                <div className="lab">{t('import.updated')}</div>
                <div className="val">{preview.updated}</div>
              </div>
              <div className="kpi">
                <div className="lab">{t('import.unchanged')}</div>
                <div className="val">{preview.unchanged}</div>
              </div>
              <div className="kpi">
                <div className="lab">{t('import.errors')}</div>
                <div className="val">{preview.errors}</div>
              </div>
            </div>
            {rowsWithIssues.length ? (
              <div className="span2 tablewrap v52-modal-table" id="importErrors">
                <table>
                  <thead>
                    <tr>
                      <th>{t('import.row')}</th>
                      <th>{t('products.cols.ref')}</th>
                      <th>{t('import.reason')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsWithIssues.map((r) => (
                      <tr key={`${r.row}-${r.ref}`}>
                        <td>{r.row ?? '—'}</td>
                        <td>{r.ref}</td>
                        <td>{t(`importReason.${r.reason ?? 'UNKNOWN'}`, { defaultValue: r.reason ?? '' })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {preview.below_cost_refs.length > 0 ? (
              <div className="pricewarning span2" id="importBelowCost">
                {t('import.belowCost', { n: preview.below_cost_refs.length, refs: preview.below_cost_refs.slice(0, 15).join(', ') })}
              </div>
            ) : null}
            <div className="help span2">{preview.errors > 0 ? t('import.blocked') : t('import.ready')}</div>
            <button type="button" className="btn black span2" id="importCommit" disabled={busy || preview.errors > 0} onClick={() => void commit()}>
              {busy ? t('common.saving') : t('import.apply')}
            </button>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
